/**
 * PHARMAKON — Terms & Conditions (trilingual: FR / AR / EN).
 *
 * Standalone page reachable from the LoginPage footer at /terms. Renders a
 * language switcher (FR · AR · EN) with Arabic rendered RTL, and the full
 * T&C body tailored to the Algerian pharmacy software regulatory context.
 *
 * Content is held in TERMS_BY_LANG below to keep the page self-contained
 * and free of i18n framework dependencies — the only consumer is this view.
 */
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon, Wordmark } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";

const LANGS = [
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "en", label: "English", dir: "ltr" },
];

export default function TermsPage() {
  const navigate = useNavigate();
  const [lang, setLang] = React.useState(() => {
    try {
      return localStorage.getItem("pharmakon_terms_lang") || "fr";
    } catch {
      return "fr";
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem("pharmakon_terms_lang", lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  const meta = LANGS.find((l) => l.code === lang) || LANGS[0];
  const content = TERMS_BY_LANG[lang] || TERMS_BY_LANG.fr;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--color-bg)" }}>
      {/* Top bar — brand + back */}
      <header className="h-14 border-b border-border-subtle bg-bg-elevated flex items-center px-5 sticky top-0 z-10">
        <Link to="/login" className="inline-flex items-center gap-2 text-text-muted hover:text-text-default transition-colors" title="Retour à la connexion">
          <Icon name="arrow-left" size={15} />
          <span className="text-xs font-medium hidden sm:inline">Retour</span>
        </Link>
        <div className="mx-auto">
          <Wordmark size="sm" />
        </div>
        {/* Language switcher */}
        <div
          role="tablist"
          aria-label="Langue du document"
          className="flex items-center gap-0.5 p-0.5 rounded-md border border-border-subtle bg-bg-sunken"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              role="tab"
              aria-selected={l.code === lang}
              onClick={() => setLang(l.code)}
              className={cn(
                "px-2.5 h-7 rounded text-2xs font-medium transition-colors",
                l.code === lang
                  ? "bg-bg-elevated text-text-strong shadow-subtle"
                  : "text-text-muted hover:text-text-default",
              )}
            >
              {l.label}
            </button>
          ))}
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 px-5 py-10">
        <article
          dir={meta.dir}
          lang={lang}
          className={cn(
            "mx-auto max-w-3xl rounded-xl border border-border-subtle bg-bg-elevated p-8 lg:p-10",
            "shadow-subtle",
          )}
        >
          {/* Document header */}
          <div className="mb-8 pb-6 border-b border-border-subtle">
            <div className="text-2xs uppercase tracking-widest text-accent font-semibold mb-2">
              {content.overline}
            </div>
            <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight text-text-strong mb-2">
              {content.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-text-muted">
              <span>
                <strong className="text-text-default">{content.versionLabel} :</strong> 2.0
              </span>
              <span>
                <strong className="text-text-default">{content.dateLabel} :</strong> 01/05/2026
              </span>
              <span>
                <strong className="text-text-default">{content.publisherLabel} :</strong> Novera Pharmakon SARL
              </span>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-7 text-sm leading-relaxed text-text-default">
            {content.sections.map((s, i) => (
              <section key={i}>
                <h2 className="text-base font-semibold text-text-strong mb-2">
                  {`${i + 1}. ${s.title}`}
                </h2>
                {s.paragraphs?.map((p, j) => (
                  <p key={j} className="mb-2">
                    {p}
                  </p>
                ))}
                {s.bullets && (
                  <ul className="list-disc ms-5 mt-2 space-y-1 text-text-default">
                    {s.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-border-subtle flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-2xs text-text-muted">
            <div>{content.footerCopy}</div>
            <a
              href="https://novera-dz.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 hover:text-accent transition-colors"
            >
              <span className="font-semibold">{content.publisherLabel}:</span>
              <span className="font-mono">novera-dz.com</span>
              <Icon name="external-link" size={11} />
            </a>
          </div>
        </article>

        <p className="mx-auto max-w-3xl mt-4 text-center text-2xs text-text-muted">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="hover:text-text-default underline-offset-2 hover:underline"
          >
            ← Retour à l'écran de connexion
          </button>
        </p>
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TERMS — content per language. Written in a professional legal register,
// tailored to the Algerian pharmacy SaaS context (CNAS / CHIFA integration,
// patient data protection under Law n° 18-07 on personal data protection,
// pharmaceutical regulation under Law n° 18-11 on health).
// ─────────────────────────────────────────────────────────────────────────────

const TERMS_BY_LANG = {
  fr: {
    overline: "Document légal",
    title: "Conditions générales d'utilisation",
    versionLabel: "Version",
    dateLabel: "Dernière mise à jour",
    publisherLabel: "Éditeur",
    footerCopy:
      "Pour toute question relative au présent document, contactez legal@novera-dz.com.",
    sections: [
      {
        title: "Objet et acceptation",
        paragraphs: [
          "Les présentes Conditions Générales d'Utilisation (« CGU ») régissent l'accès et l'utilisation de la plateforme PHARMAKON, logiciel de gestion pharmaceutique édité par Novera Pharmakon SARL (« Novera », « nous »), destiné aux officines exerçant sur le territoire de la République Algérienne Démocratique et Populaire.",
          "L'accès à la plateforme, à ses modules ou à ses APIs vaut acceptation pleine et entière des présentes CGU. À défaut d'acceptation, l'Utilisateur doit s'abstenir d'utiliser le service.",
        ],
      },
      {
        title: "Définitions",
        bullets: [
          "« Plateforme » : l'ensemble logiciel PHARMAKON, ses interfaces web, mobiles, et APIs.",
          "« Officine » : l'entité pharmaceutique titulaire de la licence d'exploitation.",
          "« Utilisateur » : toute personne physique habilitée à se connecter à la Plateforme au nom de l'Officine (propriétaire, manager, caissier, fournisseur).",
          "« Données patient » : toute information à caractère personnel concernant un client ou un assuré CHIFA, traitée dans le cadre de la dispensation.",
          "« CNAS » / « CHIFA » : Caisse Nationale des Assurances Sociales des Travailleurs Salariés et son système de tiers payant électronique.",
        ],
      },
      {
        title: "Confidentialité et protection des données",
        paragraphs: [
          "Novera s'engage à traiter les données à caractère personnel conformément à la Loi n° 18-07 du 10 juin 2018 relative à la protection des personnes physiques dans le traitement des données à caractère personnel, ainsi qu'aux référentiels de l'Autorité Nationale de Protection des Données Personnelles (ANPDP).",
          "Les Données patient sont strictement confidentielles. Elles ne sont accessibles qu'aux Utilisateurs de l'Officine concernée et aux systèmes CNAS / CHIFA autorisés par l'assuré dans le cadre de la dispensation.",
          "Aucune donnée patient n'est commercialisée, profilée, ou transférée à des tiers à des fins marketing.",
        ],
      },
      {
        title: "Infrastructure hébergée dans le cloud",
        paragraphs: [
          "La Plateforme est exploitée sur une infrastructure cloud dimensionnée pour la haute disponibilité, avec ségrégation logique des données par Officine (multi-tenant strict).",
          "Les centres de données utilisés respectent les normes ISO/IEC 27001 et SOC 2 Type II. Les sauvegardes sont chiffrées au repos (AES-256) et géo-redondantes.",
          "Novera met tout en œuvre pour atteindre une disponibilité de service de 99,9 % sur une base mensuelle, hors maintenance planifiée annoncée avec un préavis minimum de 48 heures.",
        ],
      },
      {
        title: "Chiffrement et politique de sécurité",
        bullets: [
          "Chiffrement en transit : TLS 1.2+ pour l'ensemble des communications client-serveur et serveur-CNAS.",
          "Chiffrement au repos : AES-256 sur la base de données et les sauvegardes.",
          "Authentification : mots de passe hachés avec algorithme bcrypt (coût ≥ 12), sessions à durée limitée, jetons de rafraîchissement révocables.",
          "Journalisation : toutes les actions sensibles (création, modification, suppression de données) sont auditées et conservées 5 ans.",
          "Politique de divulgation responsable : faille de sécurité à signaler à security@novera-dz.com sous embargo de 90 jours.",
        ],
      },
      {
        title: "Responsabilités de l'Utilisateur",
        paragraphs: [
          "L'Utilisateur s'engage à fournir des informations exactes lors de l'inscription et à les tenir à jour. Il est seul responsable de la confidentialité de ses identifiants.",
          "L'Utilisateur s'interdit toute utilisation contraire à la législation pharmaceutique algérienne (Loi n° 18-11 relative à la santé), notamment en matière de dispensation des psychotropes, de tenue du registre des stupéfiants, et de respect des prescriptions médicales.",
          "Toute saisie erronée, fraude documentaire CHIFA, ou détournement de l'outil engage la pleine responsabilité civile et pénale de l'Officine et de l'Utilisateur.",
        ],
      },
      {
        title: "Contrôle d'accès et sécurité du compte",
        bullets: [
          "Chaque Utilisateur dispose d'un identifiant nominatif. Le partage de compte est strictement interdit.",
          "Les rôles (Propriétaire, Manager, Caissier) déterminent les permissions effectives. Le rôle Propriétaire est seul habilité à créer ou révoquer des comptes.",
          "L'Officine doit notifier sans délai toute compromission, perte d'appareil, ou départ d'un collaborateur, afin que Novera révoque les sessions actives concernées.",
        ],
      },
      {
        title: "Conformité réglementaire",
        paragraphs: [
          "La Plateforme est conçue pour assister l'Officine dans le respect des obligations légales pharmaceutiques en Algérie, sans toutefois s'y substituer. La responsabilité du respect de la réglementation incombe au pharmacien titulaire.",
          "Les modules CHIFA, e-facture B2B et reporting d'inventaire sont mis à jour conformément aux évolutions des spécifications CNAS et de la DGI.",
        ],
      },
      {
        title: "Conservation et protection des données",
        paragraphs: [
          "Les données transactionnelles (ventes, achats, lots, mouvements de stock) sont conservées pendant la durée du contrat et 10 ans après sa résiliation, conformément aux obligations comptables algériennes.",
          "Les données patient sont conservées le temps nécessaire à l'exécution du service et purgées sur demande écrite de l'Officine sous 30 jours, sauf obligation légale de conservation contraire.",
          "En cas de cessation du service, une exportation complète des données de l'Officine (formats CSV, XLSX, JSON) est fournie sans frais dans les 60 jours.",
        ],
      },
      {
        title: "Conditions d'utilisation du service",
        bullets: [
          "L'accès au service requiert une licence en cours de validité.",
          "Tout module additionnel (Aide à la Décision, Multi-Caisses, Backup Premium…) est soumis à des conditions tarifaires distinctes.",
          "Novera se réserve le droit de suspendre temporairement le service en cas d'impayé après mise en demeure restée infructueuse pendant 15 jours.",
        ],
      },
      {
        title: "Limitations de responsabilité",
        paragraphs: [
          "Novera met en œuvre les meilleures pratiques de l'industrie mais ne peut être tenue responsable des dommages indirects, des pertes d'exploitation, ou des préjudices commerciaux résultant d'une indisponibilité ponctuelle du service.",
          "La responsabilité totale de Novera, toutes causes confondues, est plafonnée au montant des sommes effectivement versées par l'Officine au titre des 12 derniers mois.",
          "Novera ne saurait être tenue responsable d'un défaut imputable au CNAS, au CHIFA, ou à tout opérateur tiers nécessaire au fonctionnement du service.",
        ],
      },
      {
        title: "Clauses de confidentialité",
        paragraphs: [
          "Les parties s'engagent réciproquement à préserver la confidentialité de toute information non publique échangée dans le cadre de l'exécution du contrat.",
          "L'obligation de confidentialité survit à la résiliation du contrat pour une durée de cinq (5) ans.",
        ],
      },
      {
        title: "Droit applicable et juridiction compétente",
        paragraphs: [
          "Les présentes CGU sont régies par le droit algérien. Tout litige non résolu à l'amiable sera porté devant les tribunaux compétents d'Alger.",
        ],
      },
    ],
  },

  en: {
    overline: "Legal document",
    title: "Terms & Conditions of Use",
    versionLabel: "Version",
    dateLabel: "Last updated",
    publisherLabel: "Publisher",
    footerCopy:
      "For any question regarding this document, please contact legal@novera-dz.com.",
    sections: [
      {
        title: "Purpose and acceptance",
        paragraphs: [
          "These Terms & Conditions of Use (the \"Terms\") govern access to and use of the PHARMAKON platform, a pharmacy management software product published by Novera Pharmakon LLC (\"Novera\", \"we\"), and intended for pharmacies operating on the territory of the People's Democratic Republic of Algeria.",
          "Accessing the platform, its modules, or its APIs constitutes full and unreserved acceptance of these Terms. Users who do not accept must refrain from using the service.",
        ],
      },
      {
        title: "Definitions",
        bullets: [
          "\"Platform\": the PHARMAKON software product, its web and mobile interfaces, and APIs.",
          "\"Pharmacy\": the pharmaceutical entity holding the operating licence.",
          "\"User\": any individual authorised to log in on behalf of the Pharmacy (owner, manager, cashier, supplier).",
          "\"Patient Data\": any personal data relating to a customer or CHIFA-insured patient handled during dispensing.",
          "\"CNAS\" / \"CHIFA\": the National Social Security Fund for Salaried Workers and its electronic third-party-payer system.",
        ],
      },
      {
        title: "Privacy and data protection",
        paragraphs: [
          "Novera processes personal data in accordance with Algerian Law No. 18-07 of 10 June 2018 on the protection of natural persons in the processing of personal data, and with the guidelines of the National Authority for the Protection of Personal Data (ANPDP).",
          "Patient Data is strictly confidential. It is only accessible to Users of the relevant Pharmacy and to CNAS / CHIFA systems authorised by the insured patient during the dispensing process.",
          "No patient data is sold, profiled, or transferred to third parties for marketing purposes.",
        ],
      },
      {
        title: "Cloud-hosted infrastructure",
        paragraphs: [
          "The Platform runs on a cloud infrastructure sized for high availability, with strict logical data segregation per Pharmacy (strict multi-tenancy).",
          "The data centres used comply with ISO/IEC 27001 and SOC 2 Type II standards. Backups are encrypted at rest (AES-256) and geo-redundant.",
          "Novera targets a service availability of 99.9% on a monthly basis, excluding scheduled maintenance announced at least 48 hours in advance.",
        ],
      },
      {
        title: "Encryption and security policy",
        bullets: [
          "Encryption in transit: TLS 1.2+ for all client-server and server-CNAS communications.",
          "Encryption at rest: AES-256 on the database and backups.",
          "Authentication: passwords hashed with bcrypt (cost ≥ 12), time-limited sessions, revocable refresh tokens.",
          "Auditing: all sensitive actions (creation, modification, deletion of data) are audited and retained for 5 years.",
          "Responsible disclosure policy: security issues should be reported to security@novera-dz.com under a 90-day embargo.",
        ],
      },
      {
        title: "User responsibilities",
        paragraphs: [
          "The User undertakes to provide accurate information at registration and to keep it up to date. The User is solely responsible for the confidentiality of their credentials.",
          "The User shall refrain from any use contrary to Algerian pharmaceutical legislation (Law No. 18-11 on health), particularly regarding the dispensing of psychotropics, the maintenance of the narcotics register, and compliance with medical prescriptions.",
          "Any erroneous entry, CHIFA documentation fraud, or misuse of the tool engages the full civil and criminal liability of the Pharmacy and the User.",
        ],
      },
      {
        title: "Access control and account security",
        bullets: [
          "Each User has a named identifier. Account sharing is strictly prohibited.",
          "Roles (Owner, Manager, Cashier) determine effective permissions. Only the Owner role may create or revoke accounts.",
          "The Pharmacy must notify Novera without delay of any compromise, device loss, or staff departure, so that active sessions are revoked.",
        ],
      },
      {
        title: "Regulatory compliance",
        paragraphs: [
          "The Platform is designed to help the Pharmacy comply with Algerian pharmaceutical obligations, without substituting for them. Compliance responsibility rests with the licensed pharmacist.",
          "The CHIFA, B2B e-invoice and inventory reporting modules are updated in line with CNAS and DGI specification changes.",
        ],
      },
      {
        title: "Data retention and protection",
        paragraphs: [
          "Transactional data (sales, purchases, lots, stock movements) is retained for the duration of the contract and for 10 years after its termination, in accordance with Algerian accounting obligations.",
          "Patient Data is retained only for the time necessary to perform the service and is purged upon written request of the Pharmacy within 30 days, unless a contrary legal retention obligation applies.",
          "Upon service termination, a complete export of the Pharmacy's data (CSV, XLSX, JSON formats) is provided at no cost within 60 days.",
        ],
      },
      {
        title: "Service usage conditions",
        bullets: [
          "Service access requires a valid licence.",
          "Additional modules (Decision Support, Multi-Cashier, Premium Backup…) are subject to separate pricing terms.",
          "Novera reserves the right to temporarily suspend the service in the event of non-payment after a formal notice unanswered for 15 days.",
        ],
      },
      {
        title: "Limitation of liability",
        paragraphs: [
          "Novera applies industry best practices but cannot be held liable for indirect damages, loss of business, or commercial harm resulting from any temporary service unavailability.",
          "Novera's total liability, all causes combined, is capped at the amount actually paid by the Pharmacy over the last 12 months.",
          "Novera shall not be liable for any defect attributable to CNAS, CHIFA, or any third-party operator necessary for the service to function.",
        ],
      },
      {
        title: "Confidentiality clauses",
        paragraphs: [
          "The parties mutually undertake to preserve the confidentiality of any non-public information exchanged in the performance of the contract.",
          "The confidentiality obligation survives termination of the contract for a period of five (5) years.",
        ],
      },
      {
        title: "Governing law and jurisdiction",
        paragraphs: [
          "These Terms are governed by Algerian law. Any dispute not resolved amicably shall be brought before the competent courts of Algiers.",
        ],
      },
    ],
  },

  ar: {
    overline: "وثيقة قانونية",
    title: "الشروط والأحكام العامة للاستخدام",
    versionLabel: "الإصدار",
    dateLabel: "آخر تحديث",
    publisherLabel: "الناشر",
    footerCopy:
      "لأي استفسار يتعلق بهذه الوثيقة، يرجى التواصل عبر legal@novera-dz.com.",
    sections: [
      {
        title: "الموضوع والقبول",
        paragraphs: [
          "تحكم هذه الشروط والأحكام العامة للاستخدام (يشار إليها بـ «الشروط») الوصول إلى منصة فارماكون واستخدامها، وهي برنامج لإدارة الصيدليات تنشره شركة نوفيرا فارماكون ذ.م.م («نوفيرا»، «نحن»)، وهي مخصصة للصيدليات العاملة على إقليم الجمهورية الجزائرية الديمقراطية الشعبية.",
          "إن الوصول إلى المنصة أو وحداتها أو واجهاتها البرمجية يُعدّ قبولاً كاملاً وغير مشروط لهذه الشروط. وعلى من لا يقبلها الامتناع عن استخدام الخدمة.",
        ],
      },
      {
        title: "التعاريف",
        bullets: [
          "«المنصة»: مجموع برنامج فارماكون وواجهاته الويب والمحمولة وواجهاته البرمجية.",
          "«الصيدلية»: الكيان الصيدلاني الحامل لرخصة الاستغلال.",
          "«المستخدم»: كل شخص طبيعي مؤهل للولوج إلى المنصة باسم الصيدلية (مالك، مدير، أمين صندوق، مورّد).",
          "«بيانات المريض»: كل معلومة ذات طابع شخصي تخص زبوناً أو مؤمَّناً لدى شيفاء، يتم معالجتها في إطار صرف الأدوية.",
          "«CNAS» / «CHIFA»: الصندوق الوطني للضمان الاجتماعي للعمال الأجراء ونظامه الإلكتروني للدفع من الغير.",
        ],
      },
      {
        title: "السرية وحماية البيانات",
        paragraphs: [
          "تلتزم نوفيرا بمعالجة البيانات ذات الطابع الشخصي وفقاً للقانون 18-07 المؤرخ في 10 جوان 2018 المتعلق بحماية الأشخاص الطبيعيين في مجال معالجة المعطيات ذات الطابع الشخصي، وكذا للمراجع الصادرة عن السلطة الوطنية لحماية المعطيات الشخصية (ANPDP).",
          "بيانات المريض سرية بشكل صارم، ولا يمكن الاطلاع عليها إلا من قبل مستخدمي الصيدلية المعنية وأنظمة CNAS / CHIFA المرخص لها من قبل المؤمَّن في إطار صرف الدواء.",
          "لا يتم بيع أي بيانات للمرضى أو تحليلها لأغراض ربحية أو نقلها لأطراف ثالثة لأغراض تسويقية.",
        ],
      },
      {
        title: "البنية التحتية السحابية",
        paragraphs: [
          "تشتغل المنصة على بنية تحتية سحابية ذات توفر عالٍ، مع فصل منطقي صارم للبيانات بين الصيدليات (multi-tenancy صارم).",
          "تحترم مراكز البيانات المعتمدة معايير ISO/IEC 27001 وSOC 2 Type II. وتُشفَّر النسخ الاحتياطية أثناء التخزين بمعيار AES-256 وتوزَّع جغرافياً.",
          "تسعى نوفيرا لضمان توفر الخدمة بنسبة 99.9% شهرياً، باستثناء عمليات الصيانة المخططة المُعلَن عنها قبل 48 ساعة على الأقل.",
        ],
      },
      {
        title: "التشفير وسياسة الأمن",
        bullets: [
          "التشفير أثناء النقل: TLS 1.2 فأعلى لجميع الاتصالات بين العميل والخادم وبين الخادم وCNAS.",
          "التشفير أثناء التخزين: AES-256 على قاعدة البيانات والنسخ الاحتياطية.",
          "المصادقة: تُجَزَّأ كلمات المرور بخوارزمية bcrypt (تكلفة ≥ 12)، مع جلسات محدودة المدة ورموز تحديث قابلة للإلغاء.",
          "التسجيل: تُسجَّل جميع العمليات الحساسة (إنشاء، تعديل، حذف البيانات) وتُحفَظ لمدة 5 سنوات.",
          "سياسة الإفصاح المسؤول: تُرسَل الثغرات الأمنية إلى security@novera-dz.com تحت حظر نشر مدته 90 يوماً.",
        ],
      },
      {
        title: "مسؤوليات المستخدم",
        paragraphs: [
          "يلتزم المستخدم بتقديم معلومات صحيحة عند التسجيل وبتحديثها. ويتحمل وحده مسؤولية الحفاظ على سرية بيانات الدخول الخاصة به.",
          "يمتنع المستخدم عن أي استخدام يخالف التشريعات الصيدلانية الجزائرية (القانون 18-11 المتعلق بالصحة)، لا سيما فيما يتعلق بصرف المؤثرات العقلية، ومسك سجل المخدرات، واحترام الوصفات الطبية.",
          "أي إدخال خاطئ، أو تزوير في وثائق شيفاء، أو استخدام منحرف للأداة، يُرتِّب المسؤولية المدنية والجزائية الكاملة للصيدلية وللمستخدم.",
        ],
      },
      {
        title: "التحكم في الوصول وأمن الحساب",
        bullets: [
          "لكل مستخدم معرّف اسمي. وتقاسم الحساب محظور تماماً.",
          "تحدد الأدوار (مالك، مدير، أمين صندوق) الصلاحيات الفعلية. ولا يحق إلا للمالك إنشاء أو إلغاء الحسابات.",
          "على الصيدلية إبلاغ نوفيرا فوراً بأي اختراق أو فقدان لجهاز أو مغادرة لأحد العاملين، لإلغاء الجلسات النشطة المعنية.",
        ],
      },
      {
        title: "الامتثال التنظيمي",
        paragraphs: [
          "صُمِّمت المنصة لمساعدة الصيدلية على احترام الالتزامات القانونية الصيدلانية في الجزائر، دون أن تحلَّ محلّها. وتقع مسؤولية احترام التنظيم على عاتق الصيدلي الحامل للقب.",
          "تُحدَّث وحدات شيفاء، والفوترة الإلكترونية بين الشركات (B2B)، وتقارير المخزون، وفقاً لتطور مواصفات CNAS والمديرية العامة للضرائب.",
        ],
      },
      {
        title: "حفظ وحماية البيانات",
        paragraphs: [
          "تُحفَظ البيانات التعاملية (المبيعات، المشتريات، الدفعات، حركات المخزون) طوال مدة العقد ولمدة 10 سنوات بعد فسخه، وفقاً للالتزامات المحاسبية الجزائرية.",
          "لا تُحفَظ بيانات المريض إلا للمدة اللازمة لأداء الخدمة، وتُحذَف بناءً على طلب كتابي من الصيدلية في غضون 30 يوماً، باستثناء أي التزام قانوني مخالف بالحفظ.",
          "في حالة إنهاء الخدمة، تُقدَّم نسخة كاملة لتصدير بيانات الصيدلية (بصيغ CSV وXLSX وJSON) مجاناً في غضون 60 يوماً.",
        ],
      },
      {
        title: "شروط استخدام الخدمة",
        bullets: [
          "يستلزم الوصول إلى الخدمة رخصة سارية المفعول.",
          "تخضع أي وحدة إضافية (دعم اتخاذ القرار، تعدد الصناديق، النسخ الاحتياطي المميز…) لشروط تعريفية مستقلة.",
          "تحتفظ نوفيرا بحق التعليق المؤقت للخدمة في حالة عدم الدفع بعد إعذار يبقى دون رد لمدة 15 يوماً.",
        ],
      },
      {
        title: "تحديد المسؤولية",
        paragraphs: [
          "تطبق نوفيرا أفضل الممارسات في الصناعة، غير أنها لا تتحمل المسؤولية عن الأضرار غير المباشرة، أو خسائر الاستغلال، أو الأضرار التجارية الناتجة عن انقطاع مؤقت للخدمة.",
          "تُحدَّد المسؤولية الإجمالية لنوفيرا، مهما كانت الأسباب، بمبلغ المدفوعات الفعلية للصيدلية خلال الاثني عشر شهراً الأخيرة.",
          "لا تتحمل نوفيرا أي مسؤولية عن خلل ناتج عن CNAS أو CHIFA أو أي طرف ثالث ضروري لاشتغال الخدمة.",
        ],
      },
      {
        title: "بنود السرية",
        paragraphs: [
          "يلتزم الطرفان بالحفاظ على سرية كل معلومة غير علنية يتم تبادلها أثناء تنفيذ العقد.",
          "يستمر الالتزام بالسرية بعد فسخ العقد لمدة خمس (5) سنوات.",
        ],
      },
      {
        title: "القانون المطبَّق والاختصاص القضائي",
        paragraphs: [
          "تخضع هذه الشروط للقانون الجزائري. وكل نزاع لم تتم تسويته ودياً يُحال على المحاكم المختصة بالجزائر العاصمة.",
        ],
      },
    ],
  },
};
